<template>
  <Teleport to="body">
    <ClientOnly>
      <transition name="modalSlideUp">
        <div v-if="props.isOpen" class="fixed inset-0 z-inpage-modal flex items-end justify-center bg-black-transparent-60 md:items-center" @click.self="onClickOverlay">
          <div
            ref="panelRef"
            aria-modal="true"
            class="modalSlideUp-panel max-h-[90dvh] w-full max-w-[560px] overflow-y-auto bg-white font-Spectral"
            role="dialog"
            tabindex="-1"
            :aria-label="props.ariaLabel"
            :aria-labelledby="props.ariaLabelledby"
            :data-testid="props.dataTestid"
          >
            <div class="relative px-6 py-8 md:px-10">
              <button
                ref="closeButtonRef"
                aria-label="Close"
                class="absolute right-1 top-1 h-10 w-10 p-2 disabled:opacity-40"
                type="button"
                :data-testid="props.closeDataTestid"
                :disabled="props.isCloseDisabled"
                @click="props.onClose"
              >
                <img alt="" class="h-full w-full" src="@/assets/icon/close/black.svg" />
              </button>
              <slot />
            </div>
          </div>
        </div>
      </transition>
    </ClientOnly>
  </Teleport>
</template>

<script lang="ts" setup>
import { onKeyStroke, useScrollLock } from '@vueuse/core';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** 送信中など、閉じさせたくない間だけ立てる。閉じるボタン・オーバーレイ・Esc をまとめて塞ぐ */
  isCloseDisabled?: boolean;
  ariaLabel?: string;
  ariaLabelledby?: string;
  dataTestid?: string;
  closeDataTestid?: string;
}

const props = withDefaults(defineProps<Props>(), { isCloseDisabled: false, ariaLabel: undefined, ariaLabelledby: undefined, dataTestid: undefined, closeDataTestid: undefined });

const bodyElementRef = ref<HTMLElement | null>(null);
onMounted(() => {
  bodyElementRef.value = document.body;
});
const isBackgroundLocked = useScrollLock(bodyElementRef);

const panelRef = ref<HTMLElement | null>(null);
const closeButtonRef = ref<HTMLElement | null>(null);
const lastFocusedElement = ref<HTMLElement | null>(null);

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const getFocusableElements = (): HTMLElement[] => (panelRef.value ? Array.from(panelRef.value.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)) : []);

watch(
  () => props.isOpen,
  async (isOpen) => {
    isBackgroundLocked.value = isOpen;
    if (isOpen) {
      lastFocusedElement.value = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      await nextTick();
      // 取り消せない操作を載せることがあるため、開いた直後のフォーカスは中身の実行ボタンに置かない
      (closeButtonRef.value ?? panelRef.value)?.focus();
      return;
    }
    // aria-modal で背景を不活性と伝えている以上、閉じたあとのフォーカスは呼び出し元へ戻す。
    // 実行に成功して呼び出し元のボタンごと画面が入れ替わることがあるため、残っているときだけ戻す
    const returnTo = lastFocusedElement.value;
    lastFocusedElement.value = null;
    if (returnTo && document.body.contains(returnTo)) returnTo.focus();
  },
);

onBeforeUnmount(() => {
  isBackgroundLocked.value = false;
});

const onClickOverlay = () => {
  if (!props.isCloseDisabled) props.onClose();
};

onKeyStroke('Escape', () => {
  if (props.isOpen && !props.isCloseDisabled) props.onClose();
});

onKeyStroke('Tab', (event) => {
  if (!props.isOpen) return;
  const focusables = getFocusableElements();
  // 送信中は全ボタンが disabled になり候補が消える。素通しにすると背面へ抜け、
  // 送信が失敗してモーダルが残ったときにエラー文言へ戻れなくなる
  if (focusables.length === 0) {
    event.preventDefault();
    panelRef.value?.focus();
    return;
  }

  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement;
  const isInsidePanel = active instanceof Node && panelRef.value?.contains(active);

  if (event.shiftKey && (active === first || !isInsidePanel)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (active === last || !isInsidePanel)) {
    event.preventDefault();
    first.focus();
  }
});
</script>
